import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem,
  Select, Stack, Typography, Unstable_Grid2 as Grid
} from '@mui/material';
import {
  Close as CloseIcon, ArrowUpward, ArrowDownward, Send as SendIcon, PhotoCamera as PhotoIcon,
  ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';
import { PARAM_DEFS } from 'common/constants/physicalParams';
import { NOTIFY_PROGRESS_IMPROVEMENT } from 'api/graphql/physicalProgress';

// Las 3 vistas que se pueden recorrer con las flechas dentro del modal (sincronizadas en ambas fotos).
const VIEWS = [
  { key: 'front_image_url', label: 'FRENTE' },
  { key: 'lateral_image_url', label: 'LATERAL' },
  { key: 'back_image_url', label: 'ESPALDA' }
];

const entryLabel = (e) => (e?.taken_at || (e?.created_at || '').slice(0, 10) || '');
const deltaColor = (good) => {
  if (good === null) return 'text.secondary';
  return good ? 'success.main' : 'error.main';
};
const legacyPhoto = (e) => e?.after_image_url || e?.before_image_url || null;
// Foto del ángulo activo; entradas viejas (solo antes/después) caen a esa imagen.
const photoFor = (e, viewIndex) => {
  const hasNew = e?.front_image_url || e?.lateral_image_url || e?.back_image_url;
  if (hasNew) return e?.[VIEWS[viewIndex].key] || null;
  return legacyPhoto(e);
};

/**
 * Compara dos entradas de progreso: fotos lado a lado + deltas de parámetros (verde si mejora).
 * El cliente puede notificar a sus entrenadores con acceso (canNotify).
 */
function ProgressCompareModal({ open, onClose, entries, canNotify = true }) {
  const dispatch = useDispatch();
  const [notify, { loading }] = useMutation(NOTIFY_PROGRESS_IMPROVEMENT);

  const ordered = useMemo(() => [...entries].sort((a, b) => (entryLabel(a) < entryLabel(b) ? -1 : 1)), [entries]);
  const [fromId, setFromId] = useState(ordered[0]?.id);
  const [toId, setToId] = useState(ordered[ordered.length - 1]?.id);

  const [view, setView] = useState(0);

  const from = ordered.find((e) => e.id === fromId) || ordered[0];
  const to = ordered.find((e) => e.id === toId) || ordered[ordered.length - 1];

  const deltas = useMemo(() => {
    if (!from || !to) return [];
    return PARAM_DEFS.map((p) => {
      const a = from[p.key];
      const b = to[p.key];
      if (a == null || b == null) return null;
      const delta = Math.round((Number(b) - Number(a)) * 10) / 10;
      const good = delta === 0 ? null : ((delta > 0) === p.goodWhenUp);
      return { ...p, a: Number(a), b: Number(b), delta, good };
    }).filter(Boolean);
  }, [from, to]);

  const improved = deltas.some((d) => d.good === true);

  const doNotify = async () => {
    try {
      await notify({ variables: { fromId, toId } });
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `notify-${Date.now()}`, variant: 'success', message: 'Tus entrenadores fueron notificados' })));
    } catch { /* errorHandler */ }
  };

  const photo = (e, label) => {
    const src = photoFor(e, view);
    return (
      <Box sx={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', backgroundColor: 'surface.raised', aspectRatio: '3 / 4', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', borderColor: 'hairline.strong', boxShadow: (th) => th.palette.shadow.card }}>
        {src ? (
          <Box component="img" src={src} alt={label} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Stack alignItems="center" spacing={0.5} sx={{ color: 'text.disabled' }}>
            <PhotoIcon sx={{ fontSize: 30 }} />
            <Typography variant="caption">Sin foto</Typography>
          </Stack>
        )}
        <Chip size="small" label={label} sx={{ position: 'absolute', top: 6, left: 6, backgroundColor: 'rgba(0,0,0,0.6)', color: '#FFFFFF', fontWeight: 700 }} />
      </Box>
    );
  };

  const selectSx = { '& .MuiSelect-select': { py: 0.75 } };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { backgroundColor: 'surface.content', backgroundImage: 'none', border: '1px solid', borderColor: 'hairline.strong', boxShadow: (th) => th.palette.shadow.card } }}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Comparar progreso
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Grid container spacing={1.5}>
            <Grid xs={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>Desde</Typography>
              <Select fullWidth size="small" value={fromId} onChange={(e) => setFromId(e.target.value)} sx={selectSx}>
                {ordered.map((e) => <MenuItem key={e.id} value={e.id}>{entryLabel(e)}</MenuItem>)}
              </Select>
            </Grid>
            <Grid xs={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>Hasta</Typography>
              <Select fullWidth size="small" value={toId} onChange={(e) => setToId(e.target.value)} sx={selectSx}>
                {ordered.map((e) => <MenuItem key={e.id} value={e.id}>{entryLabel(e)}</MenuItem>)}
              </Select>
            </Grid>
          </Grid>

          <Box>
            <Grid container spacing={1.5}>
              <Grid xs={6}>{photo(from, entryLabel(from))}</Grid>
              <Grid xs={6}>{photo(to, entryLabel(to))}</Grid>
            </Grid>
            {/* Flechas para recorrer FRENTE / LATERAL / ESPALDA (sincronizadas en ambas fotos). */}
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} sx={{ mt: 1 }}>
              <IconButton size="small" onClick={() => setView((v) => (v - 1 + VIEWS.length) % VIEWS.length)} sx={{ color: 'secondary.main', border: '1px solid', borderColor: 'hairline.strong' }} aria-label="vista anterior"><ChevronLeftIcon /></IconButton>
              <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.08em', minWidth: 80, textAlign: 'center' }}>{VIEWS[view].label}</Typography>
              <IconButton size="small" onClick={() => setView((v) => (v + 1) % VIEWS.length)} sx={{ color: 'secondary.main', border: '1px solid', borderColor: 'hairline.strong' }} aria-label="vista siguiente"><ChevronRightIcon /></IconButton>
            </Stack>
          </Box>

          {deltas.length > 0 ? (
            <Stack spacing={0.5} sx={{ backgroundColor: 'surface.raised', borderRadius: '12px', p: 1.5, border: '1px solid', borderColor: 'hairline.main' }}>
              {deltas.map((d) => {
                const color = deltaColor(d.good);
                const Arrow = d.delta > 0 ? ArrowUpward : ArrowDownward;
                return (
                  <Stack key={d.key} direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.label}</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{d.a} → {d.b} {d.unit}</Typography>
                      <Stack direction="row" alignItems="center" sx={{ color, minWidth: 56, justifyContent: 'flex-end' }}>
                        {d.delta !== 0 && <Arrow sx={{ fontSize: 14 }} />}
                        <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                          {d.delta > 0 ? '+' : ''}{d.delta}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 1 }}>
              Las dos entradas no comparten parámetros para comparar.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button color="inherit" onClick={onClose} sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 700 }}>Cerrar</Button>
        {canNotify && (
          <Button variant="contained" color="primary" disabled={!improved || loading || fromId === toId} startIcon={<SendIcon fontSize="small" />} onClick={doNotify} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Enviar al entrenador
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

ProgressCompareModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  entries: PropTypes.arrayOf(PropTypes.object).isRequired,
  canNotify: PropTypes.bool
};

export default ProgressCompareModal;
